import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { EmptySearchResult } from '@/components/EmptySearchResult'
import InputSearch from '@/components/InputSearch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tab'
import {
  EmptyMyWorkResult,
  MyWorkCard,
  MyWorkCompletedCard,
  useMyWorkCompleted,
  useMyWorkInProgress,
  useMyWorkStore,
} from '@/features/my-work'
import { useDebounce } from '@/hooks/useDebounce'
import { cn } from '@/utils'
import { MIN_SEARCH_LENGTH, SEARCH_DEBOUNCE_DELAY_MS } from '@/utils/constants'

const GRID_CLASS = 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'

const LoadingBlock = () => (
  <div className="flex items-center justify-center py-12">
    <div className="text-gray-500">Loading...</div>
  </div>
)

const EmptyBlock = ({
  tab,
  hasSearchQuery,
}: {
  tab: 'inProgress' | 'completed'
  hasSearchQuery: boolean
}) => (
  <div className="flex flex-col items-center justify-center py-12">
    {hasSearchQuery ? (
      <EmptySearchResult
        translationKey="myWork"
        showImage
        className="text-center"
      />
    ) : (
      <EmptyMyWorkResult
        translationKey={tab}
        showImage
        className="text-center"
      />
    )}
  </div>
)

const MyWorkPage: React.FC = () => {
  const {
    activeTab,
    searchText,
    page,
    limit,
    setActiveTab,
    setSearchText,
    reset,
  } = useMyWorkStore()

  const { t } = useTranslation()

  const offset = page * limit

  const debouncedSearchText = useDebounce(searchText, SEARCH_DEBOUNCE_DELAY_MS)

  const searchQuery = useMemo(() => {
    const trimmed = debouncedSearchText?.trim() || ''
    return trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : ''
  }, [debouncedSearchText])

  const { data: inProgressData, isLoading: isLoadingInProgress } =
    useMyWorkInProgress(limit, offset, searchQuery)

  const { data: completedData, isLoading: isLoadingCompleted } =
    useMyWorkCompleted(limit, offset, searchQuery)

  const activeData = activeTab === 'inProgress' ? inProgressData : completedData
  const activeLoading =
    activeTab === 'inProgress' ? isLoadingInProgress : isLoadingCompleted

  const shouldShowSearch =
    searchText.trim().length > 0 || activeLoading || activeData.length > 0

  useEffect(() => reset, [reset])

  const handleTabChange = (value: string) => {
    setActiveTab(value as 'inProgress' | 'completed')
  }

  const tabTriggerBaseClass = useMemo(
    () =>
      cn(
        'rounded-none border-b-4 border-transparent bg-transparent px-0 pb-3 pt-2',
        'text-sm font-medium text-gray-500',
        'data-[state=active]:text-brand-secondary-700 data-[state=active]:font-medium',
        'data-[state=active]:border-brand-utility-brand-700',
        'data-[state=active]:shadow-none'
      ),
    []
  )

  const inProgressContent = useMemo(() => {
    if (isLoadingInProgress) return <LoadingBlock />
    if (inProgressData.length === 0)
      return (
        <EmptyBlock tab="inProgress" hasSearchQuery={searchQuery.length > 0} />
      )

    return (
      <div className={GRID_CLASS}>
        {inProgressData.map((item) => (
          <MyWorkCard key={item.id} item={item} />
        ))}
      </div>
    )
  }, [isLoadingInProgress, inProgressData, searchQuery])

  const completedContent = useMemo(() => {
    if (isLoadingCompleted) return <LoadingBlock />
    if (completedData.length === 0)
      return (
        <EmptyBlock tab="completed" hasSearchQuery={searchQuery.length > 0} />
      )

    return (
      <div className={GRID_CLASS}>
        {completedData.map((item) => (
          <MyWorkCompletedCard key={item.id} item={item} />
        ))}
      </div>
    )
  }, [isLoadingCompleted, completedData, searchQuery])

  return (
    <div className="flex h-[calc(100%+2rem)] -mb-8 flex-col gap-6">
      <div className="flex flex-shrink-0 justify-between">
        <div className="space-y-1">
          <h1 className="m-0 font-semibold text-display-xs text-primary-900">
            {t('myWork.title')}
          </h1>
          <p className="m-0 text-md text-tertiary-600">
            {t('myWork.description')}
          </p>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="flex flex-1 flex-col bg-transparent"
      >
        <TabsList className="w-full justify-start rounded-none border-b border-b-secondary bg-transparent p-0">
          <TabsTrigger
            value="inProgress"
            className={cn(tabTriggerBaseClass, 'mr-6')}
          >
            In Progress
          </TabsTrigger>

          <TabsTrigger value="completed" className={tabTriggerBaseClass}>
            Completed
          </TabsTrigger>
        </TabsList>

        {shouldShowSearch && (
          <div className="py-4">
            <InputSearch
              searchText={searchText}
              setSearchText={setSearchText}
              placeholder="Search"
            />
          </div>
        )}

        <div className="flex-1 overflow-auto pb-6">
          <TabsContent value="inProgress" className="mt-0">
            {inProgressContent}
          </TabsContent>

          <TabsContent value="completed" className="mt-0">
            {completedContent}
          </TabsContent>
        </div>
      </Tabs>
    </div>
  )
}

export default MyWorkPage
